import React from 'react';
import I18n from '@/modules/i18n';
import AIBadge from '@/components/shared/AIBadge';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import { TCITooltip } from '@/components/ui/Tooltip';
import useFlags from '@/hooks/useFlags';
import useAIQuestionBuilderStore from './useAIQuestionBuilderStore';

interface CreateWithAIButtonProps {
  rateLimited: boolean;
}

interface LineItemContentProps {
  disabled?: boolean;
}

const LineItemContent = ({ disabled = false }: LineItemContentProps) => (
  <>
    <span className="tw-flex tw-w-[31px] tw-shrink-0 tw-justify-center tw-mr-[5px]">
      <Icon icon="Sparkle" size={18} />
    </span>
    <Text className="tw-mr-1.5 tw-tracking-normal tw-font-tci" size="sm" tag="span">
      {I18n.t('ai_qb_create_with')}
    </Text>
    <AIBadge disabled={disabled} />
  </>
);

// AIDEV-NOTE: button layout (position:absolute inset:0, flex, padding-left:10px) comes from the
// `li a, button` rule in _question_resources.scss, scoped via the `.ai-qb-line-item` <li>. The
// classNames here only set border/bg/text-color — don't remove that SCSS or the row layout breaks.
const CreateWithAIButton = ({ rateLimited }: CreateWithAIButtonProps) => {
  const openModeSelectPanel = useAIQuestionBuilderStore(s => s.openModeSelectPanel);
  const { showAiModify } = useFlags();

  if (rateLimited) {
    return (
      <TCITooltip side="right">
        <TCITooltip.Trigger asChild>
          <button
            aria-disabled="true"
            aria-label={I18n.t('ai_qb_create_with_ai')}
            className="tw-border-none tw-bg-transparent !tw-cursor-not-allowed !tw-text-very-dark-grey"
            type="button"
            onClick={e => e.preventDefault()}
          >
            <LineItemContent disabled />
          </button>
        </TCITooltip.Trigger>
        <TCITooltip.Content>
          {I18n.t('ai_qb_rate_limit_tooltip')}
        </TCITooltip.Content>
      </TCITooltip>
    );
  }

  return (
    <button
      aria-label={I18n.t('ai_qb_create_with_ai')}
      className="tw-border-none tw-bg-transparent"
      type="button"
      onClick={() => openModeSelectPanel(showAiModify)}
    >
      <LineItemContent />
    </button>
  );
};

export default CreateWithAIButton;
